import type {
  GeoBreakdownLevel,
  PlotBreakdownLevels,
} from '@revelio/core/global-ai-assistant/breakdown-levels';
import type { EntKind } from '@revelio/data-access';

import {
  ACTIVITY_TAXONOMY_SELECTION_LIST_KEYS,
  SKILL_TAXONOMY_SELECTION_LIST_KEYS,
} from '../../../../shared/selection-lists/types';
import { type Filter, FilterId, FilterType } from '../../filters/types/filters';
import {
  getEntKindForActivityLevelId,
  getEntKindForGeoOrRicsLevelId,
  getEntKindForRoleLevelId,
} from '../../filters/utils/role-skill-ent-kind';
import { SKILL_REFERENCE_TAXONOMY_TREE_LEVELS } from '../../filters/utils/skillReferenceTaxonomyTree';
import { ROLE_DRILLDOWN_LEVEL_ORDER } from '../../filters/utils/treemapDrilldownLevels';
import type { LevelDimension, TableDimension } from '../DataProvider/types';

export type TaxonomyDimension =
  | 'role'
  | 'skills'
  | 'activities'
  | 'rics'
  | 'geography';

type LadderLevel = { level: LevelDimension; id: string };

type Taxonomy = {
  filterIds: readonly FilterId[];
  levels: readonly LevelDimension[];
  levelOf: (levelId: string) => LevelDimension | undefined;
  ladder: readonly LadderLevel[];
};

const ROLE_LEVELS: readonly LevelDimension[] = [
  'role_lvl_1',
  'role_lvl_2',
  'role_lvl_3',
  'role_lvl_4',
  'role_lvl_5',
  'role_lvl_6',
  'role_lvl_7',
];

const SKILL_LEVELS: readonly LevelDimension[] = [
  'skill_lvl_1',
  'skill_lvl_2',
  'skill_lvl_3',
  'skill_lvl_4',
  'skill_lvl_5',
  'skill_lvl_6',
  'skill_lvl_7',
  'skill_lvl_8',
];

const ACTIVITY_LEVELS: readonly LevelDimension[] = [
  'activity_lvl_1',
  'activity_lvl_2',
  'activity_lvl_3',
  'activity_lvl_4',
  'activity_lvl_5',
  'activity_lvl_6',
];

const RICS_LEVELS: readonly LevelDimension[] = [
  'rics_k10',
  'rics_k50',
  'rics_k400',
];

const GEOGRAPHY_LEVELS: readonly LevelDimension[] = [
  'region',
  'country',
  'state',
  'msa',
  'county',
  'city',
];

export const LEVELS_OF_TAXONOMY: Readonly<
  Record<TaxonomyDimension, readonly LevelDimension[]>
> = {
  role: ROLE_LEVELS,
  skills: SKILL_LEVELS,
  activities: ACTIVITY_LEVELS,
  rics: RICS_LEVELS,
  geography: GEOGRAPHY_LEVELS,
};

const TAXONOMY_OF_LEVEL: ReadonlyMap<string, TaxonomyDimension> = new Map(
  (Object.keys(LEVELS_OF_TAXONOMY) as TaxonomyDimension[]).flatMap((taxonomy) =>
    LEVELS_OF_TAXONOMY[taxonomy].map((level) => [level, taxonomy] as const)
  )
);

export const isLevelDimension = (
  dimension: TableDimension
): dimension is LevelDimension => TAXONOMY_OF_LEVEL.has(dimension);

export const taxonomyOfLevel = (level: LevelDimension): TaxonomyDimension =>
  TAXONOMY_OF_LEVEL.get(level) ?? 'geography';

export const dimensionOfLevel = (
  dimension: TableDimension
): TableDimension | TaxonomyDimension =>
  isLevelDimension(dimension) ? taxonomyOfLevel(dimension) : dimension;

const levelOfKind =
  (
    levels: readonly LevelDimension[],
    kindOf: (levelId: string) => EntKind | null
  ) =>
  (levelId: string): LevelDimension | undefined => {
    const kind = kindOf(levelId);
    return kind
      ? levels.find((level) => level.toUpperCase() === kind)
      : undefined;
  };

const ladderOf = (
  levels: readonly LevelDimension[],
  numbers: readonly number[],
  ids: readonly string[]
): LadderLevel[] =>
  numbers.reduce<LadderLevel[]>((ladder, number, index) => {
    const level = levels[number - 1];
    const id = ids[index];
    if (level && id) ladder.push({ level, id });
    return ladder;
  }, []);

const levelById = (ladder: readonly LadderLevel[]) => (levelId: string) =>
  ladder.find(({ id }) => id === levelId)?.level;

const ROLE: Taxonomy = {
  filterIds: [FilterId.ROLE],
  levels: ROLE_LEVELS,
  levelOf: levelOfKind(ROLE_LEVELS, getEntKindForRoleLevelId),
  ladder: ladderOf(ROLE_LEVELS, [1, 3, 5, 7], ROLE_DRILLDOWN_LEVEL_ORDER),
};

const SKILL_LADDER = ladderOf(
  SKILL_LEVELS,
  SKILL_REFERENCE_TAXONOMY_TREE_LEVELS,
  SKILL_TAXONOMY_SELECTION_LIST_KEYS
);

const SKILLS: Taxonomy = {
  filterIds: [FilterId.CUSTOM_SKILL],
  levels: SKILL_LEVELS,
  levelOf: levelById(SKILL_LADDER),
  ladder: SKILL_LADDER,
};

const ACTIVITIES: Taxonomy = {
  filterIds: [FilterId.ACTIVITY],
  levels: ACTIVITY_LEVELS,
  levelOf: levelOfKind(ACTIVITY_LEVELS, getEntKindForActivityLevelId),
  ladder: ladderOf(
    ACTIVITY_LEVELS,
    [3, 5, 6],
    ACTIVITY_TAXONOMY_SELECTION_LIST_KEYS
  ),
};

const RICS: Taxonomy = {
  filterIds: [FilterId.INDUSTRY, FilterId.COMPANY],
  levels: RICS_LEVELS,
  levelOf: levelOfKind(RICS_LEVELS, getEntKindForGeoOrRicsLevelId),
  ladder: ladderOf(RICS_LEVELS, [1, 2, 3], RICS_LEVELS),
};

const geographyOf = (selectionListIds: readonly string[]): Taxonomy => {
  const ladder = selectionListIds.reduce<LadderLevel[]>((found, id) => {
    const level = GEOGRAPHY_LEVELS.find((name) => name === id);
    if (level) found.push({ level, id });
    return found;
  }, []);
  return {
    filterIds: [FilterId.GEOGRAPHY],
    levels: ladder.map(({ level }) => level),
    levelOf: levelById(ladder),
    ladder,
  };
};

export const taxonomyOf = (
  dimension: TableDimension,
  geographySelectionListIds: readonly string[]
): Taxonomy | undefined => {
  switch (dimension) {
    case 'role':
      return ROLE;
    case 'skills':
      return SKILLS;
    case 'activities':
      return ACTIVITIES;
    case 'rics':
      return RICS;
    case 'geography':
      return geographyOf(geographySelectionListIds);
    default:
      return undefined;
  }
};

const RICS_CHART_LEVELS: Readonly<Record<number, LevelDimension>> = {
  10: 'rics_k10',
  50: 'rics_k50',
  400: 'rics_k400',
};

const GEOGRAPHY_CHART_LEVEL_DEPTH: Readonly<Record<GeoBreakdownLevel, number>> =
  {
    region: 0,
    country: 1,
    state: 2,
    metro: 2,
    county: 3,
    city: 3,
  };

const chartLevelOf = (
  dimension: TableDimension,
  levels: PlotBreakdownLevels | undefined,
  { ladder }: Taxonomy
): LevelDimension | undefined => {
  switch (dimension) {
    case 'role':
      return levels?.role === undefined
        ? undefined
        : ROLE_LEVELS[levels.role - 1];
    case 'skills':
      return levels?.skills === undefined
        ? undefined
        : SKILL_LEVELS[levels.skills - 1];
    case 'rics':
      return levels?.rics === undefined
        ? undefined
        : RICS_CHART_LEVELS[levels.rics];
    case 'geography': {
      const asked = levels?.geography;
      if (asked === undefined) return undefined;
      const id = asked === 'metro' ? 'msa' : asked;
      return (
        ladder.find((level) => level.id === id)?.level ??
        ladder[GEOGRAPHY_CHART_LEVEL_DEPTH[asked]]?.level ??
        GEOGRAPHY_LEVELS.find((level) => level === id)
      );
    }
    default:
      return undefined;
  }
};

const deepestSelected = (
  { filterIds, levels, levelOf }: Taxonomy,
  filters: readonly Filter[]
): number =>
  filters.reduce((deepest, filter) => {
    if (filter.type !== FilterType.TREE || !filterIds.includes(filter.id)) {
      return deepest;
    }
    return filter.selections.reduce((found, { levelId }) => {
      const level = levelOf(levelId);
      return level ? Math.max(found, levels.indexOf(level)) : found;
    }, deepest);
  }, -1);

export const getBreakdownLevel = (
  dimension: TableDimension,
  {
    filters,
    breakdownLevels,
    geographySelectionListIds,
  }: {
    filters: readonly Filter[];
    breakdownLevels?: PlotBreakdownLevels;
    geographySelectionListIds: readonly string[];
  }
): LevelDimension | undefined => {
  const taxonomy = taxonomyOf(dimension, geographySelectionListIds);
  if (!taxonomy) return undefined;
  const chartLevel = chartLevelOf(dimension, breakdownLevels, taxonomy);
  if (chartLevel) return chartLevel;

  const deepest = deepestSelected(taxonomy, filters);
  const { levels, ladder } = taxonomy;
  return (
    ladder.find(({ level }) => levels.indexOf(level) > deepest) ??
    ladder[ladder.length - 1]
  )?.level;
};

export const getBreakdownLevelPair = (
  dimension: TableDimension,
  level: LevelDimension,
  geographySelectionListIds: readonly string[]
): [LevelDimension, LevelDimension] | undefined => {
  const ladder = taxonomyOf(dimension, geographySelectionListIds)?.ladder ?? [];
  const index = ladder.findIndex((step) => step.level === level);
  if (index === -1) return undefined;
  const child = index + 1 < ladder.length ? index + 1 : index;
  const parentLevel = ladder[child - 1];
  const childLevel = ladder[child];
  return parentLevel && childLevel
    ? [parentLevel.level, childLevel.level]
    : undefined;
};

export const isSublevelOf = (
  sublevel: TableDimension,
  level: TableDimension
): boolean => {
  if (!isLevelDimension(sublevel) || !isLevelDimension(level)) return false;
  const levels = LEVELS_OF_TAXONOMY[taxonomyOfLevel(level)];
  return levels.indexOf(sublevel) > levels.indexOf(level);
};

export const findLadderLevel = (
  level: TableDimension,
  geographySelectionListIds: readonly string[]
): { id: string; depth: number } | undefined => {
  if (!isLevelDimension(level)) return undefined;
  const ladder =
    taxonomyOf(taxonomyOfLevel(level), geographySelectionListIds)?.ladder ?? [];
  const depth = ladder.findIndex((step) => step.level === level);
  const found = ladder[depth];
  return found && { id: found.id, depth };
};
